import { ChangeDetectionStrategy, Component, signal } from '@angular/core'

import { ICellRendererAngularComp } from 'ag-grid-angular'
import { ColDef, ICellRendererParams, RowEditingStartedEvent } from 'ag-grid-community'

import { FlxButtonStyleDirective, FlxThemeModule } from '@flowx/angular-theme'
import { FlxButtonComponent, FlxIconModule } from '@flowx/angular-ui-toolkit'
import { TableTemplateConfig } from '@flowx/core-sdk'

import { waitForFormControlValidation } from '../../../utils/forms'
import { FlxTableEditorComponent } from '../flx-table-editor/flx-table-editor.component'

type CellRendererParams = ICellRendererParams & { tableTemplateConfig: TableTemplateConfig }

@Component({
  selector: 'flx-table-actions',
  imports: [FlxThemeModule, FlxButtonComponent, FlxIconModule, FlxButtonStyleDirective],
  templateUrl: './flx-table-actions.component.html',
  styleUrl: './flx-table-actions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxTableActionsComponent implements ICellRendererAngularComp {
  #params: CellRendererParams

  disabled = signal(false)
  isEditing = signal(false)
  hasEditableCells = signal(false)
  hasDeletableRows = signal(false)

  ghostButtonStyles = {
    defaultState: {
      borderColor: { reference: 'color@neutrals#200' },
      iconColor: { reference: 'color@neutrals#900' },
    },
    hoverState: {
      borderColor: { reference: 'color@neutrals#400' },
      iconColor: { reference: 'color@neutrals#900' },
    },
    pressedState: {
      borderColor: { reference: 'color@neutrals#400' },
      iconColor: { reference: 'color@neutrals#900' },
    },
    disabledState: {
      borderColor: { reference: 'color@neutrals#200' },
      iconColor: { reference: 'color@neutrals#300' },
    },
  }

  keepButtonStyles = {
    defaultState: {
      backgroundColor: { reference: 'color@success#500' },
    },
    hoverState: {
      backgroundColor: { reference: 'color@success#600' },
    },
    pressedState: {
      backgroundColor: { reference: 'color@success#600' },
    },
  }

  discardButtonStyles = {
    defaultState: {
      backgroundColor: { reference: 'color@error#500' },
    },
    hoverState: {
      backgroundColor: { reference: 'color@error#600' },
    },
    pressedState: {
      backgroundColor: { reference: 'color@error#600' },
    },
  }

  agInit(params: CellRendererParams): void {
    this.#initProperties(params)
  }

  refresh(params: CellRendererParams): boolean {
    this.#initProperties(params)
    return true
  }

  onEditClick(): void {
    this.#params.api.startEditingCell({
      rowIndex: this.#params.node.rowIndex,
      colKey: this.#params.api.getAllDisplayedColumns().find((col: any) => col.colDef.editable),
    })
  }

  onDeleteClick(): void {
    this.#params.context.parentComponent.deleteRow(this.#params.node.sourceRowIndex)
  }

  async onKeepClick(): Promise<void> {
    const instances = this.#params.api.getCellEditorInstances() as FlxTableEditorComponent[]
    await Promise.all(
      instances.map((instance) =>
        instance.formControl
          ? waitForFormControlValidation(instance.formControl)
          : Promise.resolve()
      )
    )
    if (instances.every((instance) => instance.formControl?.valid)) {
      this.#params.api.stopEditing()
    }
  }

  onDiscardClick(): void {
    this.#params.api.stopEditing(true)
  }

  #initProperties(params: CellRendererParams): void {
    this.#params = params
    this.#params.api.addEventListener('rowEditingStarted', (event: RowEditingStartedEvent) => {
      if (event.rowIndex === this.#params.node.rowIndex) {
        this.isEditing.set(true)
      } else {
        this.disabled.set(true)
      }
    })
    this.#params.api.addEventListener('rowEditingStopped', () => {
      this.isEditing.set(false)
      this.disabled.set(false)
    })
    const hasEditableCells = this.#params.api
      .getColumnDefs()
      .some((colDef: ColDef) => colDef.editable)
    const hasDeletableRows =
      this.#params.tableTemplateConfig.displayOptions.flowxProps.deletableRows
    this.hasEditableCells.set(hasEditableCells)
    this.hasDeletableRows.set(hasDeletableRows)
  }
}
